import apiClient from './apiService';

export interface TestTemplate {
  id: number;
  entity_id: number;
  testing_code: string;
  // System-generated code (CTTM-N) — null until approved.
  code?: string | null;
  code_seq?: number | null;
  name?: string;
  testing_description?: string;
  cadence?: string;
  due_date_definition?: string;
  responsible_user_id?: number | null;
  accountable_user_id?: number | null;
  is_active: number;
  is_approved: number;
  control_count?: number;
  created_by?: number | null;
  created_on?: string;
  modified_by?: number | null;
  modified_on?: string;
  workflow_state?: string;
  submitted_by?: number | null;
  submitted_on?: string | null;
  submitted_by_name?: string | null;
  approved_by?: number | null;
  approved_on?: string | null;
  approved_by_name?: string | null;
  approval_note?: string | null;
  rejected_by?: number | null;
  rejected_on?: string | null;
  rejected_by_name?: string | null;
  rejection_reason?: string | null;
}

export interface TemplateControl {
  id: number;
  control_code: string;
  control_name: string;
  control_type?: string;
  category_id?: number;
}

export interface TestTemplateCreatePayload {
  testing_code: string;
  name?: string;
  testing_description?: string;
  cadence?: string;
  due_date_definition?: string;
  responsible_user_id?: number | null;
  accountable_user_id?: number | null;
  control_ids?: number[];
}

export type TestTemplateUpdatePayload = Partial<Omit<TestTemplateCreatePayload, 'control_ids'>> & {
  is_active?: number;
  is_approved?: number;
};

const BASE = '/api/v1/ctr/test-templates';

export const controlTestingService = {
  async list(search?: string): Promise<TestTemplate[]> {
    const params: Record<string, string> = { limit: '200' };
    if (search) params.search = search;
    const resp = await apiClient.get(BASE, { params });
    return resp.data?.data ?? [];
  },

  /** Approved + active CAT templates for the rollout picker (unified /dropdown). */
  async dropdown(): Promise<TestTemplate[]> {
    const resp = await apiClient.get(`${BASE}/dropdown`);
    const items = (resp.data?.items ?? []) as Array<{ id: number; name: string; code?: string }>;
    return items.map((it) => ({
      id: it.id,
      name: it.name,
      testing_code: it.code ?? '',
      is_active: 1,
    })) as TestTemplate[];
  },

  async get(id: number): Promise<TestTemplate & { controls: TemplateControl[] }> {
    const resp = await apiClient.get(`${BASE}/${id}`);
    return resp.data?.data;
  },

  async create(payload: TestTemplateCreatePayload): Promise<TestTemplate> {
    const resp = await apiClient.post(BASE, payload);
    return resp.data?.data;
  },

  async update(id: number, payload: TestTemplateUpdatePayload): Promise<TestTemplate> {
    const resp = await apiClient.put(`${BASE}/${id}`, payload);
    return resp.data?.data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async listControls(id: number): Promise<TemplateControl[]> {
    const resp = await apiClient.get(`${BASE}/${id}/controls`);
    return resp.data?.data ?? [];
  },

  async replaceScope(id: number, controlIds: number[]): Promise<void> {
    await apiClient.put(`${BASE}/${id}/controls`, { control_ids: controlIds });
  },

  // --- Plan 05c-04 / STAFF-11 ---
  // Cross-module source-row reader used by the ControlTestResponse form
  // (05c-07) to populate one response row per attached control. Mirrors
  // riskAssessmentService.listScope. NOTE: a sibling method `listControls`
  // already wraps the same endpoint with the TemplateControl type;
  // `listScope` is kept as the canonical phase-05c entrypoint with a
  // permissive index-signature row shape so the form can spread additional
  // joined columns (category_name etc.) the backend may return.
  async listScope(templateId: number): Promise<Array<{
    control_id: number;
    control_code?: string | null;
    control_name?: string | null;
    control_type?: string | null;
    category_name?: string | null;
    [key: string]: any;
  }>> {
    const resp = await apiClient.get(`${BASE}/${templateId}/controls`);
    return resp.data?.data ?? resp.data ?? [];
  },

  async rollout(templateId: number, payload: TestProgramRolloutPayload): Promise<TestProgram> {
    const resp = await apiClient.post(`${BASE}/${templateId}/rollout`, payload);
    return resp.data?.data;
  },

  async submit(id: number): Promise<void> {
    try {
      await apiClient.post(`${BASE}/${id}/submit`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to submit template');
    }
  },

  async approve(id: number, note?: string): Promise<void> {
    try {
      await apiClient.post(`${BASE}/${id}/approve`, { note: note ?? null });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to approve template');
    }
  },

  async reject(id: number, reason: string): Promise<void> {
    try {
      await apiClient.post(`${BASE}/${id}/reject`, { reason });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reject template');
    }
  },

  async reopen(id: number): Promise<void> {
    try {
      await apiClient.post(`${BASE}/${id}/reopen`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reopen template');
    }
  },
};

// =============================================================================
// Test Programs — rolled-out instances of a template
// =============================================================================

export interface TestProgramRolloutPayload {
  test_name?: string;
  maker_id: number;
  checker_id: number;
  /** @deprecated single-location field; prefer geography */
  location_id?: number;
  geography?: {
    mode: 'locations' | 'subtree' | 'type';
    location_ids?: number[];
    root_location_id?: number;
    geography_type_id?: number;
  };
  start_date: string;
  end_date: string;
}

export interface TestProgram {
  id: number;
  entity_id: number;
  template_id?: number | null;
  test_name: string;
  test_description?: string | null;
  maker_id?: number | null;
  checker_id?: number | null;
  location_id?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  due_date?: string | null;
  status?: string | null;
  is_active: number;
  template_code?: string | null;
  template_name?: string | null;
  maker_name?: string | null;
  checker_name?: string | null;
  location_name?: string | null;
  control_count?: number;
  completed_count?: number;
  workflow_state?: string | null;
  submitted_by?: number | null;
  submitted_on?: string | null;
  submitted_by_name?: string | null;
  approved_by?: number | null;
  approved_on?: string | null;
  approved_by_name?: string | null;
  approval_note?: string | null;
  rejected_by?: number | null;
  rejected_on?: string | null;
  rejected_by_name?: string | null;
  rejection_reason?: string | null;
}

export interface ProgramControlRow {
  score_id: number;
  control_id: number;
  control_code: string;
  control_name: string;
  control_type?: string | null;
  effectiveness_score?: number | null;
  existence_score?: number | null;
  test_remarks?: string | null;
  control_is_approved?: number;
}

export interface ProgramImplementationRow {
  impl_score_id: number;
  control_id: number;
  implementation_id: number;
  result?: 'Pass' | 'Fail' | 'NA' | null;
  remarks?: string | null;
  impl_is_approved?: number;
  sequence_number?: number | null;
  implementation_type?: string | null;
  target_id?: number | null;
  target_label?: string | null;
  target_subtitle?: string | null;
  explanation?: string | null;
}

export interface TestProgramDetail extends TestProgram {
  controls: ProgramControlRow[];
  implementations: ProgramImplementationRow[];
}

const PROG_BASE = '/api/v1/ctr/test-programs';

export const controlTestProgramService = {
  async list(opts?: {
    assigned_to_me?: boolean;
    checker_for_me?: boolean;
    mine?: boolean;
    status?: string;
    search?: string;
  }): Promise<TestProgram[]> {
    const params: Record<string, string> = { limit: '200' };
    if (opts?.assigned_to_me) params.assigned_to_me = 'true';
    if (opts?.checker_for_me) params.checker_for_me = 'true';
    if (opts?.mine) params.mine = 'true';
    if (opts?.status) params.status = opts.status;
    if (opts?.search) params.search = opts.search;
    const resp = await apiClient.get(PROG_BASE, { params });
    return resp.data?.data ?? [];
  },

  async get(id: number): Promise<TestProgramDetail> {
    const resp = await apiClient.get(`${PROG_BASE}/${id}`);
    return resp.data?.data;
  },

  async updateImplScore(
    programId: number,
    implScoreId: number,
    payload: { result?: 'Pass' | 'Fail' | 'NA' | null; remarks?: string | null },
  ): Promise<void> {
    await apiClient.put(`${PROG_BASE}/${programId}/impl-scores/${implScoreId}`, payload);
  },

  async updateControlScore(
    programId: number,
    scoreId: number,
    payload: { effectiveness_score?: number | null; existence_score?: number | null; test_remarks?: string | null },
  ): Promise<void> {
    await apiClient.put(`${PROG_BASE}/${programId}/control-scores/${scoreId}`, payload);
  },

  async submit(programId: number): Promise<void> {
    await apiClient.post(`${PROG_BASE}/${programId}/submit`);
  },

  async approve(programId: number): Promise<void> {
    await apiClient.post(`${PROG_BASE}/${programId}/approve`);
  },

  async reject(programId: number, reason?: string): Promise<void> {
    await apiClient.post(`${PROG_BASE}/${programId}/reject`, { reason });
  },

  async wfSubmit(id: number): Promise<void> {
    try {
      await apiClient.post(`${PROG_BASE}/${id}/wf-submit`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to submit program');
    }
  },

  async wfApprove(id: number, note?: string): Promise<void> {
    try {
      await apiClient.post(`${PROG_BASE}/${id}/wf-approve`, { note: note ?? null });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to approve program');
    }
  },

  async wfReject(id: number, reason: string): Promise<void> {
    try {
      await apiClient.post(`${PROG_BASE}/${id}/wf-reject`, { reason });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reject program');
    }
  },

  async wfReopen(id: number): Promise<void> {
    try {
      await apiClient.post(`${PROG_BASE}/${id}/wf-reopen`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reopen program');
    }
  },
};

export default controlTestingService;
